<template>
  <div class="creator-package-snippet">
    <div class="creator-package-snippet__header">
      <h3 class="global-h3 creator-package-snippet__title">
        {{ props.packageObj.name }}
      </h3>
      <!-- Price snippet -->
      <div class="price-snippet creator-package-snippet__price-snippet">
        <div class="price-snippet__starts-at">starts at</div>
        <div class="global-h3 price-snippet__price">${{ new Intl.NumberFormat("en-US").format(props.packageObj.price) }}</div>
      </div>
      <!-- / Price snippet -->
    </div>

    <div class="creator-package-snippet__content">
      <div class="creator-package-snippet__description">
        {{ props.packageObj.description }}
      </div>

      <!-- Deliverables list -->
      <div class="deliverables-list creator-package-snippet__deliverables-list">
        <h5 class="global-h5 deliverables-list__title">Deliverables</h5>

        <ul class="deliverables-list__list-itself">
          <li class="deliverables-list__list-item" v-for="listItem in props.packageObj.deliverables" v-html="DOMPurify.sanitize(listItem.replace(/\d+/gim, match => `<b>${match}</b>`))"></li>
        </ul>
      </div>
      <!-- / Deliverables list -->

      <!-- Buttons panel -->
      <div class="buttons-panel creator-package-snippet__buttons-panel">
        <div class="buttons-panel__cta-button-wrap">
          <SrpButton
            class="buttons-panel__cta-button"
            @click="
              async () => {
                if (props.isCustomerAdmin) {
                  showCustomPackageGetStartedModal = true;
                }
                emit('clickGetStarted');
              }
            "
            :isDisabled="props.isEditMode || isCreatingNewCustomCollab"
          >
            <template v-if="isCreatingNewCustomCollab" #icon><LoadingIcon :size="17" /></template>
            Get Started
          </SrpButton>
        </div>

        <div v-if="props.isEditMode" class="buttons-panel__manage-buttons-wrap">
          <SrpButton v-if="props.isEditMode" class="buttons-panel__manage-button" fill="outlined" size="tiny" @click="emit('edit', props.packageObj.id)">
            <template #icon><IconEmbedded name="pencil_2-5" :size="17" /></template>
            Edit
          </SrpButton>

          <LinkWithIcon v-if="props.isEditMode" class="buttons-panel__manage-button" @click="isDeleteModalVisible = true" color="red">
            <template v-if="isDeleting" #icon><LoadingIcon :size="21" /></template>
            <template v-else #icon><IconEmbedded name="trashcan-alt_2" :size="21" /></template>
          </LinkWithIcon>

          <!--Delete Modal-->
          <SrpModal v-model:isVisible="isDeleteModalVisible" size="small">
            <template #content>
              <h2 class="global-h2" style="margin-bottom: 10px">Delete Package</h2>
              Are you sure you want to delete this package? <br />
              This action cannot be undone.
            </template>
            <template #footer>
              <SrpButton @click="isDeleteModalVisible = false" style="margin-right: 12px" color="gray" fill="outlined" :isDisabled="isDeleting">Cancel</SrpButton>
              <SrpButton
                :isDisabled="isDeleting"
                @click="
                  async () => {
                    await deletePackage();
                    emit('delete', props.packageObj.id);
                    isDeleteModalVisible = false;
                  }
                "
                color="red"
              >
                <template #icon>
                  <LoadingIcon v-if="isDeleting" :size="19" />
                  <IconEmbedded v-else name="trashcan-alt_2-5" :size="19" />
                </template>
                Delete
              </SrpButton>
            </template>
          </SrpModal>
        </div>
      </div>
      <!-- / Buttons panel -->
    </div>

    <!--Create Custom Collab Confirm Modal-->
    <SrpModal v-model:isVisible="showCustomPackageGetStartedModal" :isCloseButtonVisible="!isCreatingNewCustomCollab">
      <template #header>
        <h2 class="global-h2">Ready to work with this creator?</h2>
      </template>
      <template #content>
        <div>
          <h3><strong>Here's what comes next:</strong></h3>
          <ol>
            <li>Get Started will create a Custom Collab and message the creator.</li>
            <li>Communicate with them to define the scope of work and deliverables.</li>
            <li>Finalize a work agreement so the creator can get to work!</li>
            <li>Review the creator's work, provide feedback, and transfer compensation.</li>
          </ol>
        </div>
      </template>
      <template #footer>
        <SrpButton :isDisabled="isCreatingNewCustomCollab" @click="handlePackageGetStarted">{{ isCreatingNewCustomCollab ? "Creating..." : "Get Started" }}</SrpButton>
        <SrpButton :isDisabled="isCreatingNewCustomCollab" @click="showCustomPackageGetStartedModal = false" color="gray" fill="outlined" style="margin-left: 10px">Back</SrpButton>
      </template>
    </SrpModal>
  </div>
</template>

<script setup lang="ts">
import { inject, ref } from "vue";
import DOMPurify from "dompurify";
import CustomerCustomCollabsRepo from "@repos/CustomerCustomCollabsRepo";
import CreatorPackagesRepo from "@repos/CreatorPackagesRepo";
import { useRouter } from "vue-router";

// Components
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SrpModal from "@components/ui/SrpModal.vue";

// Types
import { CreatorPackage } from "@contracts/creatorPackages";
import LinkWithIcon from "@components/LinkWithIcon.vue";

// Router
const router = useRouter();

const props = withDefaults(
  defineProps<{
    packageObj: CreatorPackage | null;
    isEditMode?: boolean;
    isCustomerAdmin: boolean;
    creatorProfileId: string;
    communityId?: string;
  }>(),
  {
    packageObj: null,
    isEditMode: false,
    isCustomerAdmin: false,
    creatorProfileId: "",
    communityId: "",
  }
);

const emit = defineEmits<{
  (e: "edit", packageId: string): void;
  (e: "delete", packageId: string): void;
  (e: "clickGetStarted"): void;
}>();

// Delete =====================================================================
const isDeleting = ref<boolean>(false);
const isDeleteModalVisible = ref<boolean>(false);

async function deletePackage() {
  isDeleting.value = true;
  await CreatorPackagesRepo.deletePackage(props.creatorProfileId, props.packageObj.id);
  isDeleting.value = false;
}

// Creator's Package: Get Started =============================================
const showCustomPackageGetStartedModal = ref<boolean>(false);
const isCreatingNewCustomCollab = ref<boolean>(false);

async function handlePackageGetStarted() {
  isCreatingNewCustomCollab.value = true;
  const { id } = await CustomerCustomCollabsRepo.createCustomCollabFromCreatorPackage(props.creatorProfileId, props.communityId, props.packageObj.id);
  isCreatingNewCustomCollab.value = false;

  await router.push({
    name: "CustomerCustomCollabDetails",
    params: { pageId: props.communityId, customCollabId: id },
  });
}
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Price snippet ==============================================================
.price-snippet {
  &__starts-at {
    color: rgba(0, 0, 0, 0.4);
    font-size: 12px;
    line-height: 12px;
    white-space: nowrap;
  }

  &__price {
    font-weight: 400;
    line-height: 22px;
  }
}

// Deliverables list ==========================================================
.deliverables-list {
  &__title {
    margin-bottom: 7px;
    font-weight: 500;
  }

  &__list-itself {
    gap: 4px 0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    list-style: none;
  }

  &__list-item {
  }
}

// Buttons panel ==============================================================
.buttons-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__cta-button-wrap {
    gap: 7px;
    display: flex;
    align-items: flex-start;
    flex-direction: column;
  }

  &__cta-button {
  }

  &__save-creator-first-note {
  }

  &__manage-buttons-wrap {
    flex-grow: 1;
    gap: 0 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  &__manage-button {
  }
}

// Creator package snippet ====================================================
.creator-package-snippet {
  @include standardFont;
  padding: 26px 28px 26px;
  box-sizing: border-box;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 7px 32px -10px rgba(0, 0, 0, 0.4);

  // header ---------------------------
  &__header {
    gap: 0 15px;
    padding-bottom: 22px;
    margin-bottom: 22px;
    display: flex;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: 50%;
      height: 0;
      border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
      position: absolute;
      inset: auto auto 0 0;
    }
  }

  &__title {
    flex-grow: 1;
    line-height: 24px;
  }

  &__price-snippet {
  }

  // content --------------------------
  &__content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__description {
    margin-bottom: 20px;
    font-size: 16px;
    line-height: 19px;
    white-space: pre-wrap;
  }

  &__deliverables-list {
    flex-grow: 1;
    margin-bottom: 25px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__buttons-panel {
    width: 100%;
  }
}
</style>
